import type { Dayjs } from "dayjs";
import { isNil, lte } from "lodash";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../dayjs";
import type { SavedOrder, SavedOrderWithNames } from "../orders/orderTypes";
import { DisplayMode, OrderStatus } from "./BaseEntity";

export function getFreeCancellationDays(freeCancellationDate: string | null | undefined) {
  if (!freeCancellationDate) {
    return undefined;
  }
  return dayjs(freeCancellationDate, ISO_DATE_FORMAT).diff(dayjs(), "days");
}

export function getOrderDisplayMode(
  order: Pick<SavedOrder, "freeCancellationDate" | "isHistorical" | "status" | "contactDate" | "cancellationDate">,
): DisplayMode {
  if (order.isHistorical) {
    return DisplayMode.HISTORICAL;
  }
  const freeCancellationDays = getFreeCancellationDays(order.freeCancellationDate);
  const contactDate = dayjs(order.contactDate, ISO_DATE_FORMAT);
  const contactDisplayMode = getDisplayModeForDate(contactDate);
  if (isOverdue(order, freeCancellationDays) || contactDisplayMode === DisplayMode.OVERDUE) {
    return DisplayMode.OVERDUE;
  }
  if (isWarning(order, freeCancellationDays) || contactDisplayMode === DisplayMode.WARNING) {
    return DisplayMode.WARNING;
  }
  return contactDisplayMode;
}

type AdvancePaymentOrder = Pick<SavedOrderWithNames, "advance" | "paymentSum" | "status">;
type OrderListDisplayModeOrder = Parameters<typeof getOrderDisplayMode>[0] & AdvancePaymentOrder;

const ADVANCE_EXCLUDED_STATUSES: readonly OrderStatus[] = [
  OrderStatus.CANCELLED,
  OrderStatus.CANCELLATION_SENT,
  OrderStatus.TO_CANCEL,
  OrderStatus.PAID,
];
const ADVANCE_WARNING_DAYS = 7;
const DISPLAY_MODE_SEVERITY: readonly DisplayMode[] = [DisplayMode.NORMAL, DisplayMode.WARNING, DisplayMode.OVERDUE];

/** An advance is awaiting payment when it is expected, has a deadline, nothing was paid yet and the order is active. */
export function isAdvanceAwaitingPayment(order: AdvancePaymentOrder): boolean {
  const { amount, deadline } = order.advance;
  return (
    !!amount && amount > 0 && !!deadline && order.paymentSum <= 0 && !ADVANCE_EXCLUDED_STATUSES.includes(order.status)
  );
}

/** OVERDUE once the advance deadline has passed, WARNING from 7 days before the deadline up to the deadline day. */
export function getAdvanceDisplayMode(order: AdvancePaymentOrder, today: Dayjs = dayjs()): DisplayMode {
  const { deadline } = order.advance;
  if (!isAdvanceAwaitingPayment(order) || !deadline) {
    return DisplayMode.NORMAL;
  }
  if (deadline < today.format(ISO_DATE_FORMAT)) {
    return DisplayMode.OVERDUE;
  }
  if (deadline <= today.add(ADVANCE_WARNING_DAYS, "days").format(ISO_DATE_FORMAT)) {
    return DisplayMode.WARNING;
  }
  return DisplayMode.NORMAL;
}

/** Display mode of an order on the agents' order list: the more severe of the general and the advance display mode. */
export function getOrderListDisplayMode(order: OrderListDisplayModeOrder, today: Dayjs = dayjs()): DisplayMode {
  const orderDisplayMode = getOrderDisplayMode(order);
  if (orderDisplayMode === DisplayMode.HISTORICAL) {
    return orderDisplayMode;
  }
  const advanceDisplayMode = getAdvanceDisplayMode(order, today);
  return DISPLAY_MODE_SEVERITY.indexOf(advanceDisplayMode) > DISPLAY_MODE_SEVERITY.indexOf(orderDisplayMode)
    ? advanceDisplayMode
    : orderDisplayMode;
}

function isWarning(order: Pick<SavedOrder, "status">, freeCancellationDays: number | undefined): boolean {
  return freeCancellationDays !== undefined && lte(freeCancellationDays, 10) && order.status === OrderStatus.INITIAL;
}

function isOverdue(order: Pick<SavedOrder, "status">, freeCancellationDays: number | undefined): boolean {
  return freeCancellationDays !== undefined && lte(freeCancellationDays, 0) && order.status === OrderStatus.INITIAL;
}

export function formatDate(
  date: string | null | undefined,
  {
    sourceFormat = ISO_DATE_FORMAT,
    outputFormat = DATE_FORMAT,
  }: {
    sourceFormat?: string;
    outputFormat?: string;
  } = {},
) {
  if (isNil(date)) {
    return "";
  }
  const m = dayjs(date, sourceFormat);
  if (m.isValid()) {
    return m.format(outputFormat);
  }
  return date;
}

function getDisplayModeForDate(date: Dayjs): DisplayMode {
  const now = dayjs();
  if (now.add(1, "days").isAfter(date)) {
    return DisplayMode.OVERDUE;
  }
  if (now.add(5, "days").isAfter(date)) {
    return DisplayMode.WARNING;
  }
  return DisplayMode.NORMAL;
}
